import {
  getCustomTag,
  useModuleFederationContext,
  useNavigation,
  useRouteParams,
  useTranslation,
  useViewBaseMetadata,
} from '@msdk/common'
import {
  CustomActionPosition,
  EntityMetadata,
  EntityModel,
  readBaseMetadataByNameAndModule,
  readEntityModelIncludeExternal,
  ViewAcl,
  ViewModel,
} from '@msdk/definitions'
import {
  ActionsContainer,
  AddIcon,
  ButtonPrimary,
  ButtonScheleton,
  COLORS,
  HeaderWrapper,
  IconSelector,
  styled,
  THEME_CONST,
  Tooltip,
  Typography,
} from '@msdk/ui'
import React, { useEffect, useState } from 'react'

import { useFormModal, useViewActionButtons } from '../../hooks'
import { resolveItem, stringFormat } from '../../utils'
import { ImportedComponentErrorBoundary } from '../ImportedComponentBoundary'
import { ViewActions } from '../ViewActions'
import { useViewContext } from '../ViewContextProvider'
import { ViewHeaderLinks } from '../ViewHeaderLinks'

const StyledHeaderWrapper = styled(HeaderWrapper)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: start;
  /* max-height: ${THEME_CONST.viewHeaderMaxHeight}px; */
  background-color: ${COLORS.white.main};
  gap: 20px;
`

const StyledHeaderContainer = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: row;
  min-height: 40px;
`

const HeaderLinksContainer = styled('div')`
  margin-right: auto;
  padding-left: 20px;
  padding-top: 5px;
`

const HeaderLabelWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  gap: 8px;
`

const IconContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`
interface Props {
  entityName: string
  viewName: string
  description?: string
  mode: 'Page' | 'Modal'
  isInitialized: boolean
}

export function ViewHeader(props: Props) {
  const getModel = (entityName: string, viewName: string) => {
    const baseMetadata = readBaseMetadataByNameAndModule(entityName, viewName) as EntityMetadata
    return readEntityModelIncludeExternal(entityName, baseMetadata.msProvider) as EntityModel
  }

  const t = useTranslation()
  const entityModel = getModel(props.entityName, props.viewName)
  const viewModel = entityModel.getView(props.viewName) as ViewModel
  const navigate = useNavigation()
  const { createModal: createFormModal } = useFormModal()
  const viewContext = useViewContext()
  const [lastAcl, setLastAcl] = useState<ViewAcl | undefined>(undefined)
  const [lastCanCreate, setLastCanCreate] = useState(false)

  const { itemId } = useRouteParams()

  const onCreate = () => {
    switch (props.mode) {
      case 'Page': {
        const form = entityModel.createForm ?? entityModel.editForm ?? entityModel.defaultForm
        const ctx: Record<string, string> = {}
        const qs = new URLSearchParams()
        qs.append('mode', 'Edit')

        if (itemId) {
          // Add the current sitemap item id to the context so that
          // when we go back from the form we can find the correct item
          // to go back to
          ctx['sId'] = itemId
        }

        navigate(`${entityModel.name}/${form}/Create`, viewModel.associatedSiteMap, ctx, qs)
        break
      }
      case 'Modal': {
        const modalRef = createFormModal(
          entityModel.name,
          entityModel.createForm ?? entityModel.editForm ?? entityModel.defaultForm,
          'Create',
          () => {
            viewContext.context.fetchData!()
          },
          { mode: 'Edit', modalForm: viewModel.modalForm },
        )
        modalRef.open()
        break
      }
      default:
        break
    }
  }

  const headerLabel = props.description ?? t(viewModel.idml, viewModel.description)

  const tryGetCustomCreateButtonLabel = () => {
    const { labels } = viewModel
    if (labels?.createButton) {
      return t(labels.createButton.idml, labels.createButton.description)
    }
  }

  useEffect(() => {
    if (props.isInitialized) {
      const acl = viewContext.context.acl
      setLastAcl(acl)
      setLastCanCreate((entityModel.canBeCreated && viewModel.canBeCreated && acl?.canCreate) ?? false)
    }
  }, [props.isInitialized])

  const customActions = useViewActionButtons(
    viewModel,
    CustomActionPosition.OutsideView,
    viewContext.context.reload!,
    viewContext.context.selectedRows,
    lastAcl,
  )

  const hasTooltip = !!viewModel.tooltip && !!viewModel.tooltip.idml && !!viewModel.tooltip.description
  const hasSubtitle = !!viewModel.subtitle && !!viewModel.subtitle.idml && !!viewModel.subtitle.description
  const modules = useModuleFederationContext()
  const baseMetadata = useViewBaseMetadata(props.entityName, props.viewName) as EntityMetadata
  const msModule = modules.loadedModules[baseMetadata.msProvider]
  const ImportedComponent = msModule ? resolveItem(msModule, viewModel.header?.customUI) : undefined

  return (
    <StyledHeaderWrapper>
      <StyledHeaderContainer>
        {!ImportedComponent ? (
          <HeaderLabelWrapper>
            <Typography variant="h3">{headerLabel}</Typography>
            {hasTooltip && (
              <Tooltip
                title={
                  <Typography key={viewModel.tooltip?.idml} variant="body2" color={COLORS.white.main}>
                    {t(viewModel.tooltip!.idml, viewModel.tooltip!.description)}
                  </Typography>
                }
                arrow
                placement="right"
              >
                <IconContainer>
                  <IconSelector iconName="InfoOutlined" color={COLORS.primary.main} />
                </IconContainer>
              </Tooltip>
            )}
          </HeaderLabelWrapper>
        ) : (
          <ImportedComponentErrorBoundary>
            <ImportedComponent viewModel={viewModel} />
          </ImportedComponentErrorBoundary>
        )}
        <HeaderLinksContainer>
          <ViewHeaderLinks links={viewModel.headerLinks} reload={viewContext.context.reload as () => Promise<void>} />
        </HeaderLinksContainer>
        <ActionsContainer>
          {!lastAcl && <ButtonScheleton />}
          {lastAcl && (
            <ViewActions
              actions={customActions}
              entityName={viewModel.entity}
              viewName={viewModel.name}
              reload={viewContext.context.reload!}
              selectedRows={viewContext.context.selectedRows!}
              currentFilters={viewContext.context.currentFilters!}
              acl={lastAcl}
              legacyActivateDeleteBehaviour={entityModel.showBinnedItemsFilter}
            />
          )}
          {entityModel.canBeCreated && viewModel.canBeCreated && lastCanCreate && (
            <ButtonPrimary
              startIcon={<AddIcon />}
              onClick={onCreate}
              data-test-id={getCustomTag(['view', 'create', 'button'])}
            >
              {tryGetCustomCreateButtonLabel() ??
                stringFormat(
                  t('CMPSR_CREATE_ENTITY', 'Create {0}'),
                  t(entityModel.idml, entityModel.description).toLowerCase(),
                )}
            </ButtonPrimary>
          )}
        </ActionsContainer>
      </StyledHeaderContainer>
      {hasSubtitle && (
        <Typography key={viewModel.subtitle?.idml} variant="body2" color={COLORS.text.secondary}>
          {t(viewModel.subtitle!.idml, viewModel.subtitle!.description)}
        </Typography>
      )}
    </StyledHeaderWrapper>
  )
}
